import type { FormEvent } from 'react'
import type { MatchingCriteria } from 'types/match'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import copy from 'copy.json'
import { addParamToPath, dimmBackground } from 'utils'
import { adminPanelPaths, paths } from 'paths'
import { CrossIcon } from 'icons'

import Button from 'components/Button'
import Input from 'components/Input'
import RangeSlider from 'components/RangeSlider'
import Spinner from 'components/Spinner'
import FileUploadWrapper from 'components/layout/FileUploadWrapper'
import NoMatchingOffersModal from 'modals/NoMatchingOffers'
import IndividualEnergyPricingErrorModal from 'modals/IndividualEnergyPricingErrorModal'
import CsvFormatGuide from 'components/CsvFormatGuide'
import { individualPricingCsvGuide } from 'components/csvFormatGuides'

const button = copy.common.button

export default function SearchMatchingOffers() {
  const { api } = useStore()
  const navigate = useNavigate()
  const { demandId } = useParams()
  const [loading, setLoading] = useState(false)
  const [surplusPercentageLimit, setSurplusPercentageLimit] = useState(45)
  const [
    maximalNumberOfProductionEnergyProfilesInDiff,
    setMaximalNumberOfProductionEnergyProfilesInDiff
  ] = useState(10)
  const [noMatchingOffersModal, setNoMatchingOffersModal] = useState(false)
  const [pricingFile, setPricingFile] = useState<File | null>(null)
  const [individualPricingFileErrors, setIndividualPricingFileErrors] = useState<string[]>([])
  const [pricingFileErrorModalOpen, setPricingFileErrorModalOpen] = useState(false)

  useEffect(dimmBackground)

  const submit = async (event?: FormEvent<HTMLFormElement>) => {
    if (event) {
      event.preventDefault()
    }

    setLoading(true)

    const requestData: MatchingCriteria = {
      surplus_percentage_limit: surplusPercentageLimit,
      maximal_number_of_production_energy_profiles_in_diff:
        maximalNumberOfProductionEnergyProfilesInDiff
    }

    delete requestData.pricingFile

    const formData = new FormData()

    formData.append(
      'request',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )

    if (pricingFile) formData.append('pricingFile', pricingFile)

    const response = await api.findBestMatch(Number(demandId), formData)

    const isResponseError = !!response?.individual_energy_pricing_file_errors?.length

    setLoading(false)

    if (isResponseError) {
      setIndividualPricingFileErrors(response?.individual_energy_pricing_file_errors)
      setPricingFileErrorModalOpen(true)
    }

    if (!isResponseError) {
      response?.match_result
        ? navigate(addParamToPath(adminPanelPaths.matches, response.match_result.match_id))
        : setNoMatchingOffersModal(true)
    }
  }

  const handleFileChange = (event: FormEvent<HTMLInputElement> | null) => {
    const currentTarget = event?.currentTarget

    if (currentTarget?.files) {
      const file = currentTarget.files[0]

      setIndividualPricingFileErrors([])
      setPricingFile(file)

      return
    }

    resetInput()
  }

  const resetInput = () => {
    const form = document.getElementById('range-form') as HTMLFormElement
    form.reset()
    setPricingFile(null)
  }

  return (
    <>
      <Content>
        <h1>{copy.page.search_matching_offers.heading}</h1>
        <span>
          <p>{copy.page.search_matching_offers.paragraph_1}</p>
          <p>{copy.page.search_matching_offers.paragraph_2}</p>
        </span>

        <RangeForm onSubmit={submit} id="range-form">
          <p>{copy.page.search_matching_offers.range_form.surplus_percentage_limit}</p>

          <RangeSlider
            id="surplus_percentage_limit"
            min={0}
            max={100}
            value={surplusPercentageLimit}
            step={1}
            label="%"
            displayScale
            onChange={setSurplusPercentageLimit}
          />

          <p>
            {
              copy.page.search_matching_offers.range_form
                .maximal_number_of_production_energy_profiles_in_diff
            }
          </p>

          <RangeSlider
            id="maximal_number_of_production_energy_profiles_in_diff"
            min={1}
            max={20}
            value={maximalNumberOfProductionEnergyProfilesInDiff}
            step={1}
            label=""
            displayScale
            onChange={setMaximalNumberOfProductionEnergyProfilesInDiff}
          />

          <p>{copy.page.search_matching_offers.individual_energy_pricing}</p>

          <CsvFormatGuide definition={individualPricingCsvGuide} />

          <FileUploadWrapper>
            <InputWrapper>
              <Input name="file" type="file" onChange={handleFileChange} accept=".csv" />

              {pricingFile && (
                <ResetButton onClick={resetInput} type="button">
                  <CrossIcon />
                </ResetButton>
              )}
            </InputWrapper>
          </FileUploadWrapper>

          <ButtonsWrapper>
            <Button type="submit">
              {loading && !pricingFileErrorModalOpen ? <Spinner size={16} /> : button.search}
            </Button>

            <Button secondary onClick={() => navigate('..')}>
              {button.close}
            </Button>
          </ButtonsWrapper>
        </RangeForm>
      </Content>

      <NoMatchingOffersModal
        isModalOpen={noMatchingOffersModal}
        onModalClose={() => setNoMatchingOffersModal(false)}
        onModalConfirmation={() => navigate(`../${paths.active_offers}/${demandId}`)}
      />

      <IndividualEnergyPricingErrorModal
        isModalOpen={pricingFileErrorModalOpen}
        onModalClose={() => setPricingFileErrorModalOpen(false)}
        onHandleFileChange={handleFileChange}
        onModalContinue={submit}
        errors={individualPricingFileErrors}
        fileName={pricingFile?.name}
        loading={loading}
      />
    </>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;

  width: 100%;
  max-width: 868px;
  padding: 36px 109px;
  margin: 24px auto;

  background: var(--white);
  border-radius: 4px;

  h1 {
    font: var(--600-24);
    text-align: center;
  }

  span p {
    text-align: center;
  }
`

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  gap: 8px;
  width: 100%;
  max-width: 312px;
`

const RangeForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 15px;
  padding: 32px 64px;

  width: 100%;
  max-width: 630px;

  > div {
    width: 100%;
  }

  > p {
    font: var(--600-16);
    margin: 0;
  }
`

const InputWrapper = styled.div`
  position: relative;

  > input {
    padding-right: 35px;
  }
`

const ResetButton = styled.button`
  background: transparent;
  cursor: pointer;
  border: none;
  color: var(--text);

  position: absolute;
  right: 5px;
  top: 35%;
`
